/**
 * Topic article list — the browsable per-domain list between the articles
 * menu and the reader (mobile ArticleBrowseScreen, web /articles/topic/:id).
 *
 * Articles have no required sequence: the user picks. The list is filtered by
 * All / Unread / Read / Bookmarked, and every row carries its access decision
 * so both apps lock, badge and route taps identically.
 *
 * Ordering is bundle array order within the domain (same as readingPath and
 * filterToFreeSet). Legacy free caps follow filterToFreeSet: a missing
 * `num_articles` is UNCAPPED, 0 means nothing is free.
 *
 * Pure and platform-agnostic: callers pass already-fetched plain objects and,
 * under metering, the app's per-article access function. No SDKs, no UI.
 */
import { buildReadingPaths, estimateReadingMinutes, formatMinutesLeft } from './readingPath';

/** Filter chips, in display order. */
export const TOPIC_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
  { key: 'bookmarked', label: 'Bookmarked' },
];

const FILTER_KEYS = TOPIC_FILTERS.map((f) => f.key);

/** Unknown / missing filter → 'all' (e.g. a hand-edited web ?filter= query). */
export function normalizeTopicFilter(filter) {
  return FILTER_KEYS.includes(filter) ? filter : 'all';
}

/** Progress set id the reader records for a list-opened session (both apps). */
export function topicListSetId(domainId) {
  return `topic_${domainId}`;
}

/** Empty-state copy for a filter with no rows. */
export function topicFilterEmptyCopy(filter) {
  switch (normalizeTopicFilter(filter)) {
    case 'unread':
      return "You've read every article in this domain.";
    case 'read':
      return "You haven't read any articles in this domain yet.";
    case 'bookmarked':
      return 'No bookmarks in this domain yet. Tap the bookmark icon on an article to save it here.';
    default:
      return 'No articles in this domain yet.';
  }
}

/**
 * Card preview text: the article summary when present, otherwise the body with
 * simple markdown (headings, emphasis, code ticks, quotes, dashes) stripped and
 * whitespace collapsed. Apps clamp it to 3 lines.
 */
export function articlePreviewText(article) {
  if (article?.summary) return String(article.summary);
  const body = article?.article_body || '';
  return String(body).replace(/[#*_`>\-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function idSet(ids) {
  return new Set((ids ?? []).map((id) => String(id)));
}

/**
 * Row access → lock chip + tap routing.
 *  - lock: 'none' | 'lockable' (metered, unlock available) | 'locked' (metered,
 *    exhausted / anonymous consumed) | 'legacy_locked' (beyond the free cap)
 *  - tapAction: 'open' (straight to the reader) | 'unlock_flow' (hand the
 *    article to the app's useArticleUnlockFlow.attempt — it owns auto-spend,
 *    confirm-last, exhausted dialog and the signup sheet) | 'upsell' (legacy
 *    premium dialog)
 */
function rowAccess({ index, cap, isSubscribe, meteringEnabled, getAccess, articleId }) {
  if (isSubscribe) return { lock: 'none', tapAction: 'open' };
  if (meteringEnabled) {
    // Fail closed when the app forgot to pass an access function.
    const access = typeof getAccess === 'function' ? getAccess(articleId)?.access : 'locked';
    if (access === 'unlocked') return { lock: 'none', tapAction: 'open' };
    if (access === 'lockable') return { lock: 'lockable', tapAction: 'unlock_flow' };
    return { lock: 'locked', tapAction: 'unlock_flow' };
  }
  if (cap != null && index >= cap) return { lock: 'legacy_locked', tapAction: 'upsell' };
  return { lock: 'none', tapAction: 'open' };
}

function matchesFilter(row, filter) {
  switch (filter) {
    case 'unread':
      return !row.read;
    case 'read':
      return row.read;
    case 'bookmarked':
      return row.bookmarked;
    default:
      return true;
  }
}

/**
 * Build the topic list view model.
 *
 * @param {Object} params
 * @param {Array}  params.articles        bundled ARTICLES (all domains)
 * @param {Object} params.domain          APP_DOMAIN_LIST row ({ id, domain_name, num_articles })
 * @param {Object} params.readMap         { [articleId]: { read } | truthy }
 * @param {Array}  params.bookmarks       bookmarked article ids
 * @param {string} params.filter          'all' | 'unread' | 'read' | 'bookmarked'
 * @param {boolean} params.isSubscribe
 * @param {boolean} params.meteringEnabled
 * @param {Function} [params.getAccess]   (articleId) => { access } — required under metering
 * @returns {{ filter, rows, counts: { all, unread, read, bookmarked },
 *             progress: { total, readCount, completionPct, remainingMinutes, label, show } }}
 */
export function buildTopicArticleList({
  articles,
  domain,
  readMap,
  bookmarks,
  filter,
  isSubscribe,
  meteringEnabled,
  getAccess,
}) {
  const activeFilter = normalizeTopicFilter(filter);
  const domainId = domain?.id;
  const cap = domain?.num_articles;
  const bookmarked = idSet(bookmarks);

  // Read state + minutes come from the reading spine so the list, the landing
  // menu bar and the Continue-reading hero can't disagree.
  const path = domain
    ? buildReadingPaths({ articles, domainList: [domain], readMap, articlesProgress: [] })[0]
    : null;
  const items = path?.items ?? [];
  const byId = new Map(
    (articles ?? [])
      .filter((a) => String(a?.topic_id) === String(domainId))
      .map((a) => [String(a.id), a])
  );

  const allRows = items.map((item) => {
    const article = byId.get(String(item.id));
    return {
      id: item.id,
      article,
      title: item.title,
      preview: articlePreviewText(article),
      minutes: estimateReadingMinutes(article?.word_count),
      read: item.read,
      bookmarked: bookmarked.has(String(item.id)),
      ...rowAccess({ index: item.index, cap, isSubscribe, meteringEnabled, getAccess, articleId: item.id }),
    };
  });

  const counts = {
    all: allRows.length,
    unread: allRows.filter((r) => !r.read).length,
    read: allRows.filter((r) => r.read).length,
    bookmarked: allRows.filter((r) => r.bookmarked).length,
  };

  const total = path?.total ?? 0;
  const readCount = path?.readCount ?? 0;
  const completionPct = path?.completionPct ?? 0;
  const remainingMinutes = path?.remainingMinutes ?? 0;
  const left = formatMinutesLeft(remainingMinutes);

  return {
    filter: activeFilter,
    rows: allRows.filter((r) => matchesFilter(r, activeFilter)),
    counts,
    progress: {
      total,
      readCount,
      completionPct,
      remainingMinutes,
      label: `${completionPct}% read${left ? ` · ${left}` : ''}`,
      // Same rule as the landing menu rows: hide the bar until something is read.
      show: total > 0 && readCount > 0,
    },
  };
}

/**
 * What the reader gets when a row is opened: the FILTERED rows as a snapshot,
 * at the tapped article. Legacy-locked rows are excluded (the pager has no
 * legacy lock page); metered locked rows stay in (they render as unlock
 * interstitials, which never spend). Tapping a legacy-locked row → null.
 *
 * @param {Object} params
 * @param {Array}  params.rows       rows from buildTopicArticleList (already filtered)
 * @param {string|number} params.articleId
 * @returns {{ openList: Array, index: number } | null}
 */
export function resolveTopicOpenTarget({ rows, articleId }) {
  const openRows = (rows ?? []).filter((r) => r.lock !== 'legacy_locked' && r.article);
  const index = openRows.findIndex((r) => String(r.id) === String(articleId));
  if (index < 0) return null;
  return { openList: openRows.map((r) => r.article), index };
}
